import type { SemanticTone } from '../../components/ui';
import type {
  AiBudget,
  AiCapability,
  AiFeature,
  HealthReport,
  ProviderCheck,
  ProviderHealth,
  ProviderStatus,
} from '../../types/aiOps';

/* ------------------------------------------------------------------ */
/* Money — cents are stored, money is displayed                        */
/* ------------------------------------------------------------------ */

/** 1234 → "$12.34", 0 → "$0.00", 0.4 → "<$0.01" (never "0.004 cents") */
export function formatMoney(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const value = Number(cents);
  if (!Number.isFinite(value)) return '—';
  if (value === 0) return '$0.00';
  if (Math.abs(value) < 1) return value > 0 ? '<$0.01' : '>−$0.01';
  const dollars = value / 100;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Cents as the dollars an admin types into a field */
export const centsToDollars = (cents: number): string => (Math.round(cents) / 100).toFixed(2);

export const dollarsToCents = (dollars: string | number): number => Math.round(Number(dollars) * 100);

/** 0.8 → "80%" */
export function formatShare(share: number | null | undefined): string {
  const value = Number(share);
  if (!Number.isFinite(value)) return '—';
  return `${Math.round(value * 100)}%`;
}

/** 12345 → "12,345" */
export const formatCount = (value: number | null | undefined): string =>
  value !== null && value !== undefined && Number.isFinite(Number(value)) ? Number(value).toLocaleString('en-US') : '—';

/** 1450 → "1.5s", 420 → "0.4s" */
export function formatLatency(ms: number | null | undefined): string {
  const value = Number(ms);
  if (!Number.isFinite(value) || value <= 0) return '';
  return `${(value / 1000).toFixed(1)}s`;
}

/* ------------------------------------------------------------------ */
/* Months                                                              */
/* ------------------------------------------------------------------ */

const pad = (value: number) => String(value).padStart(2, '0');

/** "2026-09" for the month the clock is in */
export const currentMonth = (now: Date = new Date(Date.now())): string => `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;

/** "2026-09" + -1 → "2026-08" */
export function shiftMonth(month: string, delta: number): string {
  const [year, index] = month.split('-').map(Number);
  if (!year || !index) return month;
  const date = new Date(Date.UTC(year, index - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;
}

/** "2026-09" → "September 2026" */
export function monthLabel(month: string): string {
  const [year, index] = (month || '').split('-').map(Number);
  if (!year || !index) return month || '';
  return new Date(Date.UTC(year, index - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Nothing has been spent in a month that has not happened yet */
export const isFutureMonth = (month: string, now: Date = new Date(Date.now())): boolean => month > currentMonth(now);

/** "2026-09-03" → "3", for the day axis */
export function dayNumber(period: string): string {
  const day = (period || '').split('-')[2];
  return day ? String(Number(day)) : period;
}

/** "2026-09-03" → "Sep 3" */
export function dayLabel(period: string): string {
  const [year, month, day] = (period || '').split('-').map(Number);
  if (!year || !month || !day) return period || '';
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/* ------------------------------------------------------------------ */
/* Dates — an outage has to be dateable                                */
/* ------------------------------------------------------------------ */

const DAY = 86_400_000;

/**
 * "Tuesday 14:20" within the past week, "Mar 12 14:20" before that.
 * A named day is what makes a failure actionable; "3d ago" is not.
 */
export function sinceLabel(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = new Date(iso);
  const time = then.getTime();
  if (Number.isNaN(time)) return '';
  const clock = then.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  const diff = now - time;
  if (diff >= 0 && diff < 6 * DAY) {
    return `${then.toLocaleDateString('en-US', { weekday: 'long' })} ${clock}`;
  }
  return `${then.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} ${clock}`;
}

/** "2h ago", "3d ago", then a plain date. Empty for null, never "Invalid Date". */
export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = now - then;
  if (diff < 0) return sinceLabel(iso, now);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(diff / DAY);
  if (days < 7) return `${days}d ago`;
  return sinceLabel(iso, now);
}

/** "in 4h", "in 3d", or "due now" once the moment has passed */
export function timeUntil(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = then - now;
  if (diff <= 0) return 'due now';
  const hours = Math.round(diff / 3_600_000);
  if (hours < 1) return 'in under an hour';
  if (hours < 48) return `in ${hours}h`;
  return `in ${Math.round(diff / DAY)}d`;
}

/**
 * When a check started failing, in words: "failing since Tuesday 14:20", or
 * "never worked" when it has not once succeeded.
 */
export function failingSince(check: ProviderCheck, now: number = Date.now()): string {
  if (check.ok) return '';
  if (!check.lastOkAt) return 'never worked';
  const since = sinceLabel(check.lastOkAt, now);
  return since ? `failing since ${since}` : 'never worked';
}

/** The oldest "last worked" across a provider's failing checks */
export function providerFailingSince(provider: ProviderHealth, now: number = Date.now()): string {
  const failing = provider.checks.filter((check) => !check.ok);
  if (failing.length === 0) return '';
  if (failing.every((check) => !check.lastOkAt)) return 'never worked';
  const oldest = failing
    .map((check) => check.lastOkAt)
    .filter((value): value is string => !!value)
    .sort()[0];
  return oldest ? `failing since ${sinceLabel(oldest, now)}` : 'never worked';
}

/* ------------------------------------------------------------------ */
/* Labels                                                              */
/* ------------------------------------------------------------------ */

export const CAPABILITIES: AiCapability[] = ['text', 'webSearch', 'image'];

export const CAPABILITY_LABEL: Record<AiCapability, string> = {
  text: 'Text',
  webSearch: 'Web search',
  image: 'Image',
};

export const capabilityLabel = (capability: AiCapability | string): string =>
  CAPABILITY_LABEL[capability as AiCapability] ?? String(capability);

export const FEATURE_LABEL: Record<AiFeature, string> = {
  monitoring_scan: 'Monitoring scans',
  monitoring_digest: 'Monitoring digests',
  intelligence_report: 'Intelligence reports',
  intelligence_news: 'News lookups',
  intelligence_ask: 'Ask questions',
  intelligence_brief: 'Briefs',
  intelligence_discover: 'Discovery',
  intelligence_influence: 'Influence assessments',
  issue_assess: 'Issue assessments',
  issue_brief: 'Issue briefs',
  government_structure: 'Government structure',
  government_officials: 'Office holders',
  government_verify: 'Second checks',
  government_match: 'Matching to the directory',
  government_duplicates: 'Finding duplicates',
  business_card: 'Business cards',
  connection_test: 'Connection tests',
  other: 'Other',
};

export const featureLabel = (feature: AiFeature | string): string =>
  FEATURE_LABEL[feature as AiFeature] ??
  String(feature)
    .replace(/_/g, ' ')
    .replace(/^./, (character) => character.toUpperCase());

/** The words a status reads as — colour never carries this on its own */
export const STATUS_LABEL: Record<ProviderStatus, string> = {
  ok: 'Working',
  degraded: 'Partly working',
  failing: 'Failing',
  unchecked: 'Never checked',
  unconfigured: 'No key',
};

export const statusLabel = (status: ProviderStatus | string): string =>
  STATUS_LABEL[status as ProviderStatus] ?? String(status);

/**
 * The word above a provider's checks, derived from those checks.
 *
 * The backend sends its own summary, but a row that says "partly working"
 * over three failed checks is exactly the dishonesty this page exists to
 * remove — so the word is computed from what is on screen.
 */
export function effectiveStatus(provider: ProviderHealth): ProviderStatus {
  if (!provider.configured) return 'unconfigured';
  const ran = provider.checks.filter((check) => !!check.checkedAt);
  if (ran.length === 0) return 'unchecked';
  if (ran.every((check) => check.ok)) return 'ok';
  if (ran.every((check) => !check.ok)) return 'failing';
  return 'degraded';
}

/** Always text, web search, image — the same order for every provider */
export const orderChecks = (checks: ProviderCheck[]): ProviderCheck[] =>
  [...checks].sort((a, b) => CAPABILITIES.indexOf(a.capability) - CAPABILITIES.indexOf(b.capability));

export const statusTone = (status: ProviderStatus | string): SemanticTone => {
  if (status === 'ok') return 'green';
  if (status === 'degraded') return 'amber';
  if (status === 'failing') return 'red';
  return 'neutral';
};

/* ------------------------------------------------------------------ */
/* The one line at the top                                             */
/* ------------------------------------------------------------------ */

export type HeadlineKind = 'no_keys' | 'never_checked' | 'no_web_search' | 'attention' | 'ok';

export interface Headline {
  kind: HeadlineKind;
  /** The state, in a sentence */
  title: string;
  /** What it costs the app, or what to do about it */
  detail: string;
  tone: 'error' | 'quiet';
}

/**
 * What an admin needs to read first. "No provider can search the web" is the
 * state that silently empties monitoring, so it is never left inside a row.
 */
export function headlineFor(report: HealthReport, now: number = Date.now()): Headline {
  const configured = report.providers.filter((provider) => provider.configured);
  if (configured.length === 0) {
    return {
      kind: 'no_keys',
      title: 'No provider has a key saved',
      detail: 'Add a key under Providers below — until then no AI feature can run.',
      tone: 'error',
    };
  }
  if (!report.checkedAt) {
    return {
      kind: 'never_checked',
      title: 'Providers have never been checked',
      detail: 'Run a check to find out what actually answers.',
      tone: 'quiet',
    };
  }
  if (!report.researchReady) {
    const since = configured
      .flatMap((provider) => provider.checks.filter((check) => check.capability === 'webSearch' && !check.ok))
      .map((check) => check.lastOkAt)
      .filter((value): value is string => !!value)
      .sort()[0];
    return {
      kind: 'no_web_search',
      title: 'No provider can search the web',
      detail: since
        ? `Monitoring scans and research collect nothing — failing since ${sinceLabel(since, now)}.`
        : 'Monitoring scans and research collect nothing.',
      tone: 'error',
    };
  }
  if (report.needsAttention) {
    const broken = report.providers.filter((provider) => {
      const status = effectiveStatus(provider);
      return status === 'failing' || status === 'degraded';
    });
    return {
      kind: 'attention',
      title: broken.length === 1 ? `${broken[0].label} is failing some checks` : `${broken.length} providers are failing checks`,
      detail: 'Web search still works, so monitoring keeps running.',
      tone: 'error',
    };
  }
  return {
    kind: 'ok',
    title: 'Every check passed',
    detail: report.checkedAt ? `Last checked ${timeAgo(report.checkedAt, now)}.` : '',
    tone: 'quiet',
  };
}

/* ------------------------------------------------------------------ */
/* Budget                                                              */
/* ------------------------------------------------------------------ */

/** The form holds dollars and percentages; the API holds cents and 0–1 shares */
export interface BudgetForm {
  cap: string;
  warn: string;
  stop: string;
}

export const toBudgetForm = (budget: AiBudget): BudgetForm => ({
  cap: centsToDollars(budget.monthlyCapCents),
  warn: String(Math.round(budget.warnAt * 100)),
  stop: String(Math.round(budget.pauseScheduledAt * 100)),
});

export const fromBudgetForm = (form: BudgetForm): AiBudget => ({
  monthlyCapCents: dollarsToCents(form.cap),
  warnAt: Number(form.warn) / 100,
  pauseScheduledAt: Number(form.stop) / 100,
});

export type BudgetErrors = Partial<Record<keyof BudgetForm, string>>;

/** The one-line reason a value is refused, keyed by field */
export function validateBudget(form: BudgetForm): BudgetErrors {
  const errors: BudgetErrors = {};

  const cap = Number(form.cap);
  if (form.cap.trim() === '') errors.cap = 'Required';
  else if (Number.isNaN(cap)) errors.cap = 'Must be a number';
  else if (cap < 0) errors.cap = 'Cannot be negative';

  const shares: Array<{ key: 'warn' | 'stop'; value: number }> = [
    { key: 'warn', value: Number(form.warn) },
    { key: 'stop', value: Number(form.stop) },
  ];
  shares.forEach(({ key, value }) => {
    if (form[key].trim() === '') errors[key] = 'Required';
    else if (Number.isNaN(value)) errors[key] = 'Must be a number';
    else if (value < 0 || value > 100) errors[key] = 'Between 0 and 100';
  });

  if (!errors.warn && !errors.stop && Number(form.warn) >= Number(form.stop)) {
    errors.warn = 'Warn before scheduled work stops';
  }

  return errors;
}

export const budgetChanged = (form: BudgetForm, saved: AiBudget): boolean => {
  const next = fromBudgetForm(form);
  return (
    next.monthlyCapCents !== saved.monthlyCapCents ||
    next.warnAt !== saved.warnAt ||
    next.pauseScheduledAt !== saved.pauseScheduledAt
  );
};

/** A ceiling of 0 is "no limit" — never "$0.00 of $0.00" */
export const hasCap = (budget: AiBudget | null | undefined): boolean => !!budget && budget.monthlyCapCents > 0;
